import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiJson } from "@/api/client.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { OfficialLogo } from "@/components/OfficialLogo.jsx";
import { statusLabel } from "@/lib/statusLabel.js";

const ESSENTIAL_KEYS = [
  "imp_reservation", "imp_avenza", "grp_eau_bouteille", "grp_eau", "eff_literie", "eff_sac_couchage",
  "eff_serviettes", "eff_telephone", "eff_fils", "grp_frontale", "eff_premiers", "vet_pluie",
  "vet_bottes_rando", "vet_bottes_pluie", "eff_jumelles", "epi_nourriture", "epi_glaciere",
];
const CAT_ORDER = ["important", "vetements", "hiver", "effets", "groupe", "epicerie", "chasse"];
const TRIP = "trip_terrain_3j";

export function PreparationPage() {
  const { ui } = useOrignalUi();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(params.get("full") === "1" ? "all" : "essentials");
  const [flash, setFlash] = useState("");
  const [fullOpen, setFullOpen] = useState(params.get("full") === "1");

  async function reload() {
    setData(await api(`/api/trips/${TRIP}/preparation`));
  }
  useEffect(() => { reload().catch(() => {}); }, []);

  const myId = user?.member?.id || user?.member_id || "mem_marcel";

  async function patch(item, body) {
    const res = await apiJson(`/api/checklist/items/${item.id}`, { ...body, id: item.id }, "PATCH");
    if (body.status === "ready" || body.status === "loaded" || body.status === "confirmed") setFlash(item.id);
    if (!res._offline) await reload();
    else setData((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, ...body } : i)) }));
  }

  const peopleById = Object.fromEntries((data?.people || []).map((p) => [p.id, p]));
  const items = (data?.items || []).filter((i) => i.status !== "not_needed" && !(i.template_item_key || "").startsWith("avz_"));
  const progress = data?.progress || {};
  const essentials = items.filter((i) => ESSENTIAL_KEYS.includes(i.template_item_key));
  const mine = items.filter((i) => i.assignee_id === myId);
  const critical = items.filter((i) => i.item_kind === "critical");

  const fullRows = useMemo(() => {
    let rows = items;
    if (filter === "essentials") rows = essentials;
    else if (filter === "mine") rows = mine;
    else if (filter === "critical") rows = critical;
    else if (filter !== "all") rows = rows.filter((i) => i.status === filter);
    return rows;
  }, [items, filter, essentials, mine, critical]);

  if (!data) return <main className="og-cockpit" data-testid="page-prep"><p className="og-hint">{ui.app.loading}</p></main>;

  return (
    <main className="og-cockpit og-cockpit--prep" data-testid="page-prep">
      <section className="og-panel" data-testid="prep-progress">
        <div className="og-identity">
          <OfficialLogo size="page" decorative />
          <strong data-testid="prep-ready">{progress.essentials_ready ?? 0} / {progress.essentials_total || 14} {ui.prep.essentialsReady}</strong>
        </div>
        <p>{progress.essentials_open ?? 0} {ui.prep.toLoad} · {progress.critical_open} {ui.prep.criticalLeft}</p>
        <div className="og-prep-bar"><span style={{ width: `${progress.essentials_total ? Math.round((progress.essentials_ready / progress.essentials_total) * 100) : 0}%` }} /></div>
        <section className="og-nq__promo" data-testid="prep-nordquest">
          <h2>{ui.nordquest.title}</h2>
          <p>{ui.nordquest.subtitle}</p>
          <Link className="og-btn og-btn--primary" to="/preparation/nordquest" data-testid="prep-nordquest-open">{ui.nordquest.open}</Link>
          <p className="og-hint" data-historical="prospect" data-testid="nq-history">{ui.nordquest.history}</p>
        </section>
        {data.source ? <a className="og-btn" href={data.source.url} target="_blank" rel="noreferrer" data-testid="prep-pdf">{ui.prep.openPdf}</a> : null}
        <button type="button" className="og-btn" data-testid="open-sepaq" onClick={() => { setFullOpen(true); setFilter("all"); setParams({ full: "1" }); }}>{ui.cockpit.sepaqFull}</button>
        <button type="button" className="og-btn" data-testid="mode-loading" onClick={() => setFullOpen(true)}>{ui.prep.loading}</button>
      </section>
      <section className="og-panel og-scroll" data-testid="prep-essentials">
        <h2>{ui.prep.essentials}</h2>
        {essentials.map((item) => (
          <ItemCard key={item.id} item={item} ui={ui} peopleById={peopleById} flash={flash === item.id} onPatch={patch} />
        ))}
      </section>
      <section className="og-panel og-scroll" data-testid="prep-mine">
        <h2>{ui.prep.filterMine}</h2>
        <button type="button" className="og-chip" data-testid="filter-mine" onClick={() => setFilter("mine")}>{ui.prep.filterMine}</button>
        {mine.map((item) => (
          <ItemCard key={item.id} item={item} ui={ui} peopleById={peopleById} flash={flash === item.id} onPatch={patch} />
        ))}
      </section>
      <section className="og-panel og-scroll" data-testid="prep-critical">
        <h2>{ui.prep.filterCrit}</h2>
        {critical.map((item) => (
          <ItemCard key={item.id} item={item} ui={ui} peopleById={peopleById} flash={flash === item.id} onPatch={patch} />
        ))}
        <h3>{ui.prep.who}</h3>
        {(progress.who || []).map((w) => (
          <p key={w.id}>{w.first_name}{w.count ? ` — ${w.count}` : ""}</p>
        ))}
      </section>
      {fullOpen ? (
        <aside className="og-drawer og-drawer--wide" data-testid="prep-full">
          <h2>{ui.cockpit.sepaqFull}</h2>
          <div className="og-chips" data-testid="prep-filters">
            {["essentials", "all", "mine", "critical"].map((f) => (
              <button key={f} type="button" className={`og-chip${filter === f ? " is-active" : ""}`} onClick={() => setFilter(f)}>
                {f === "essentials" ? ui.prep.essentials : f === "all" ? ui.prep.seeFull : f === "mine" ? ui.prep.filterMine : ui.prep.filterCrit}
              </button>
            ))}
          </div>
          {CAT_ORDER.map((cat) => {
            const rows = fullRows.filter((i) => i.category === cat);
            if (!rows.length) return null;
            return (
              <section key={cat} className={`og-prep-cat og-prep-cat--${cat}`} data-testid={`prep-cat-${cat}`}>
                <h3>{ui.prep.cats[cat]}</h3>
                {rows.map((item) => (
                  <ItemCard key={item.id} item={item} ui={ui} peopleById={peopleById} flash={flash === item.id} onPatch={patch} />
                ))}
              </section>
            );
          })}
          <button type="button" className="og-btn og-btn--primary" data-testid="load-all" onClick={() => fullRows.filter((i) => i.status !== "loaded").forEach((i) => patch(i, { status: "loaded" }))}>{ui.prep.markLoaded}</button>
          <button type="button" className="og-btn" onClick={() => { setFullOpen(false); setParams({}); }}>{ui.app.close}</button>
        </aside>
      ) : null}
    </main>
  );
}

function ItemCard({ item, ui, peopleById, flash, onPatch }) {
  const who = peopleById[item.assignee_id];
  return (
    <article className={`og-prep-item${item.item_kind === "critical" ? " is-crit" : ""}${flash ? " is-flash" : ""}`} data-testid={`prep-item-${item.template_item_key || item.id}`}>
      <div>
        <strong>{item.name}</strong>
        <small>{who ? who.first_name : ui.status.to_plan}</small>
        <span className="og-badge og-badge--ghost">{statusLabel(ui, item.status)}</span>
      </div>
      <div className="og-prep-item__actions">
        <button type="button" className="og-btn" onClick={() => onPatch(item, { status: "ready" })} data-testid={`ready-${item.id}`}>{ui.prep.markReady}</button>
        <button type="button" className="og-btn og-btn--primary" onClick={() => onPatch(item, { status: "loaded" })} data-testid={`loaded-${item.id}`}>{ui.prep.markLoaded}</button>
      </div>
    </article>
  );
}
